import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { toast } from 'sonner';
import { Loader2, ClipboardCheck, RefreshCw } from 'lucide-react';
import { safeJson } from '../lib/safeJson';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const APPROVE_REASONS = ['Movement Allowed', 'On Duty', 'Official Work', 'Other'];

export const AttendancePendingApprovals = ({ entityId }) => {
  const [records, setRecords] = useState([]);
  const [decided, setDecided] = useState([]);
  const [view, setView] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // record being decided
  const [decision, setDecision] = useState('approve');
  const [markAs, setMarkAs] = useState('present');
  const [reasonPick, setReasonPick] = useState('Movement Allowed');
  const [customReason, setCustomReason] = useState('');
  const [deduction, setDeduction] = useState('lop');
  const [leaveType, setLeaveType] = useState('Casual Leave');
  const [leaveTypes, setLeaveTypes] = useState(['Casual Leave']);
  const [saving, setSaving] = useState(false);
  const [sel, setSel] = useState({});               // bulk selection of flagged records
  const [bulkOpen, setBulkOpen] = useState(false);
  const selectedIds = Object.keys(sel).filter(id => sel[id]);

  const headers = () => {
    const token = localStorage.getItem('hrms-token');
    return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  };

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, dRes] = await Promise.all([
        fetch(`${API_URL}/api/attendance/pending-approvals${entityId ? `?entity_id=${entityId}` : ''}`,
          { headers: headers(), credentials: 'include' }),
        fetch(`${API_URL}/api/attendance/decided-approvals${entityId ? `?entity_id=${entityId}` : ''}`,
          { headers: headers(), credentials: 'include' }),
      ]);
      const data = await safeJson(pRes);
      if (pRes.ok) setRecords(Array.isArray(data) ? data : []);
      else toast.error(data.detail || 'Failed to load pending approvals');
      const dData = await safeJson(dRes);
      if (dRes.ok) setDecided(Array.isArray(dData) ? dData : []);
    } catch { toast.error('Failed to load pending approvals'); }
    finally { setLoading(false); }
  }, [entityId]);

  const handleRedecide = async (rec) => {
    if (!window.confirm(`Undo the decision for ${rec.employee_name} on ${rec.date}? The record goes back to Pending (any leave deducted is restored) and you can decide again.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/attendance/${rec.attendance_id}/revert-decision`, {
        method: 'POST', headers: headers(), credentials: 'include'
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Failed to revert');
      toast.success(d.message);
      await fetchPending();
      setView('pending');
      openDecide({ ...rec, status: 'pending_approval' });
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const openDecide = async (rec) => {
    setDialog(rec);
    setDecision('approve');
    setMarkAs('present');
    setReasonPick('Movement Allowed');
    setCustomReason('');
    setDeduction('lop');
    setLeaveType('Casual Leave');
    try {
      const res = await fetch(`${API_URL}/api/leaves/balance/${rec.employee_id}`, { headers: headers(), credentials: 'include' });
      const bals = await safeJson(res);
      if (res.ok && Array.isArray(bals) && bals.length > 0) {
        const types = bals.filter(b => (b.balance || 0) > 0).map(b => `${b.leave_type}`);
        if (types.length > 0) { setLeaveTypes(types); setLeaveType(types[0]); return; }
      }
      setLeaveTypes(['Casual Leave', 'Earned Leave', 'Medical Leave']);
    } catch { setLeaveTypes(['Casual Leave', 'Earned Leave', 'Medical Leave']); }
  };

  const submitBulk = async () => {
    const reason = reasonPick === 'Other' ? customReason.trim() : reasonPick;
    if (reasonPick === 'Other' && !reason) { toast.error('Enter a custom reason'); return; }
    if (decision === 'reject' && !reason) { toast.error('Give a reason when disapproving'); return; }
    setSaving(true);
    try {
      const body = decision === 'approve'
        ? { attendance_ids: selectedIds, decision: 'approve', mark_as: markAs, reason }
        : { attendance_ids: selectedIds, decision: 'reject', deduction, leave_type: leaveType, reason };
      const res = await fetch(`${API_URL}/api/attendance/bulk-decision`, {
        method: 'POST', headers: headers(), credentials: 'include', body: JSON.stringify(body)
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Bulk decision failed');
      toast.success(d.message);
      (d.failed || []).forEach(f => toast.error(`${f.attendance_id}: ${f.error}`));
      setSel({});
      setBulkOpen(false);
      fetchPending();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const openBulk = (kind) => {
    setDecision(kind);
    setMarkAs('present');
    setReasonPick('Movement Allowed');
    setCustomReason('');
    setDeduction('lop');
    setLeaveType('Casual Leave');
    setLeaveTypes(['Casual Leave', 'Earned Leave', 'Medical Leave']);
    setBulkOpen(true);
  };

  const submit = async () => {
    const reason = reasonPick === 'Other' ? customReason.trim() : reasonPick;
    if (decision === 'approve' && reasonPick === 'Other' && !reason) { toast.error('Enter a custom reason'); return; }
    setSaving(true);
    try {
      const body = decision === 'approve'
        ? { decision: 'approve', mark_as: markAs, reason }
        : { decision: 'reject', deduction, leave_type: leaveType, reason };
      const res = await fetch(`${API_URL}/api/attendance/${dialog.attendance_id}/decision`, {
        method: 'POST', headers: headers(), credentials: 'include', body: JSON.stringify(body)
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Failed to save decision');
      toast.success(d.message);
      setDialog(null);
      fetchPending();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Card data-testid="attendance-pending-approvals">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-lg">
          <ClipboardCheck className="w-5 h-5 text-amber-500" />
          Attendance Approvals
        </CardTitle>
        <Button size="sm" variant="outline" onClick={fetchPending} data-testid="refresh-pending-approvals">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2 mb-3">
          <Button size="sm" variant={view === 'pending' ? 'default' : 'outline'}
            className={view === 'pending' ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}
            onClick={() => setView('pending')} data-testid="att-view-pending">
            Pending ({records.length})
          </Button>
          <Button size="sm" variant={view === 'decided' ? 'default' : 'outline'}
            className={view === 'decided' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
            onClick={() => setView('decided')} data-testid="att-view-decided">
            Decided ({decided.length})
          </Button>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          {view === 'pending'
            ? 'Check-ins/outs outside the allowed window (early out, late in, half-day) land here. Approve as present / half-day with a reason, or reject with a leave-balance deduction or LOP.'
            : 'Already-decided records. Use Re-decide to undo a decision (restores any deducted leave) and decide again.'}
        </p>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : view === 'decided' ? (
          decided.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8" data-testid="no-decided-approvals">No decided records yet</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>In / Out</TableHead>
                    <TableHead>Flags</TableHead>
                    <TableHead>Decision</TableHead>
                    <TableHead>Decided By</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {decided.map((r) => {
                    const hd = r.hr_decision || {};
                    const approved = hd.decision === 'approve';
                    return (
                      <TableRow key={r.attendance_id} data-testid={`decided-att-${r.attendance_id}`}>
                        <TableCell>
                          <p className="font-medium text-sm">{r.employee_name}</p>
                          <code className="text-[10px] text-slate-400">{r.employee_id}</code>
                        </TableCell>
                        <TableCell className="text-sm">{r.date}</TableCell>
                        <TableCell className="text-xs">{r.check_in || '—'} / {r.check_out || '—'}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(r.flags || []).map((f, i) => (
                              <Badge key={i} variant="outline" className="text-[10px] border-amber-400 text-amber-700">{f}</Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={approved ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                            {approved
                              ? `Approved${hd.mark_as === 'half_day' ? ' (Half Day)' : ''}`
                              : `Rejected — ${hd.deduction === 'lop' ? 'LOP' : `${hd.deduction === 'half_day_leave' ? '0.5' : '1'}d ${hd.leave_type || 'leave'}`}`}
                          </Badge>
                          {hd.reason && <p className="text-[10px] text-slate-400 mt-0.5">{hd.reason}</p>}
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">
                          {hd.decided_by_name || '—'}
                          <p className="text-[10px] text-slate-400">{(hd.decided_at || '').slice(0, 10)}</p>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" className="h-7 text-blue-600 hover:bg-blue-50"
                            onClick={() => handleRedecide(r)} data-testid={`redecide-btn-${r.attendance_id}`}>
                            Re-decide
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )
        ) : records.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-8" data-testid="no-pending-approvals">No pending attendance approvals 🎉</p>
        ) : (
          <div className="overflow-x-auto">
            {/* Bulk decide — same decision for every selected flagged record */}
            <div className="mb-3 rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2" data-testid="att-bulk-bar">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-slate-500">Select all flagged as:</span>
                {Array.from(new Set(records.flatMap(r => r.flags || []))).map(flag => {
                  const ids = records.filter(r => (r.flags || []).includes(flag)).map(r => r.attendance_id);
                  const allOn = ids.length > 0 && ids.every(id => sel[id]);
                  return (
                    <Button key={flag} size="sm" variant={allOn ? 'default' : 'outline'} className="h-7 text-xs"
                      onClick={() => setSel(prev => {
                        const m = { ...prev };
                        ids.forEach(id => { m[id] = !allOn; });
                        return m;
                      })}
                      data-testid={`att-bulk-flag-${flag.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                      {flag} ({ids.length})
                    </Button>
                  );
                })}
                <Button size="sm" variant="outline" className="h-7 text-xs"
                  onClick={() => {
                    const allOn = records.every(r => sel[r.attendance_id]);
                    const m = {};
                    records.forEach(r => { m[r.attendance_id] = !allOn; });
                    setSel(m);
                  }} data-testid="att-bulk-select-all">Select all</Button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium" data-testid="att-bulk-count">{selectedIds.length} selected</span>
                <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={selectedIds.length === 0} onClick={() => openBulk('approve')} data-testid="att-bulk-approve">
                  Approve Selected
                </Button>
                <Button size="sm" variant="outline" className="h-7 border-red-500 text-red-600 hover:bg-red-50"
                  disabled={selectedIds.length === 0} onClick={() => openBulk('reject')} data-testid="att-bulk-reject">
                  Disapprove Selected
                </Button>
                {selectedIds.length > 0 && (
                  <Button size="sm" variant="ghost" className="h-7" onClick={() => setSel({})} data-testid="att-bulk-clear">Clear</Button>
                )}
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>In / Out</TableHead>
                  <TableHead>Why flagged</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((r) => (
                  <TableRow key={r.attendance_id} data-testid={`pending-att-${r.attendance_id}`}>
                    <TableCell>
                      <input type="checkbox" className="w-4 h-4 accent-[#DC2626]"
                        checked={!!sel[r.attendance_id]}
                        onChange={(e) => setSel(prev => ({ ...prev, [r.attendance_id]: e.target.checked }))}
                        data-testid={`att-bulk-check-${r.attendance_id}`} />
                    </TableCell>
                    <TableCell>
                      <p className="font-medium">{r.employee_name}</p>
                      <code className="text-xs text-slate-500">{r.employee_id}</code>
                    </TableCell>
                    <TableCell className="text-sm">{r.date}</TableCell>
                    <TableCell className="text-sm">{r.check_in || '—'} / {r.check_out || '—'}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(r.flags || []).map((f, i) => (
                          <Badge key={i} variant="outline" className="text-[10px] border-amber-400 text-amber-700">{f}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" className="h-7 bg-indigo-600 hover:bg-indigo-700 text-white"
                        onClick={() => openDecide(r)} data-testid={`decide-btn-${r.attendance_id}`}>
                        Decide
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Bulk decide dialog — one decision applied to every selected flagged record */}
      <Dialog open={bulkOpen} onOpenChange={o => !o && setBulkOpen(false)}>
        <DialogContent className="max-w-lg" data-testid="att-bulk-dialog">
          <DialogHeader>
            <DialogTitle>
              {decision === 'approve' ? 'Approve' : 'Disapprove'} {selectedIds.length} flagged record(s)
            </DialogTitle>
            <DialogDescription>
              The same decision and reason are applied to each selected record.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-1">
            {decision === 'approve' ? (
              <div>
                <Label className="text-xs">Mark day as</Label>
                <Select value={markAs} onValueChange={setMarkAs}>
                  <SelectTrigger data-testid="att-bulk-mark-as"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="present">Full Present (no deduction)</SelectItem>
                    <SelectItem value="half_day">Half Day</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <>
                <div>
                  <Label className="text-xs">Deduction</Label>
                  <Select value={deduction} onValueChange={setDeduction}>
                    <SelectTrigger data-testid="att-bulk-deduction"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lop">Loss of Pay (mark absent)</SelectItem>
                      <SelectItem value="half_day_leave">Half day from leave balance</SelectItem>
                      <SelectItem value="full_day_leave">Full day from leave balance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {deduction !== 'lop' && (
                  <div>
                    <Label className="text-xs">Leave type to deduct</Label>
                    <Select value={leaveType} onValueChange={setLeaveType}>
                      <SelectTrigger data-testid="att-bulk-leave-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {leaveTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}
            <div>
              <Label className="text-xs">Reason</Label>
              <Select value={reasonPick} onValueChange={setReasonPick}>
                <SelectTrigger data-testid="att-bulk-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {APPROVE_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              {reasonPick === 'Other' && (
                <Input className="mt-2" placeholder="Type the reason" value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)} data-testid="att-bulk-custom-reason" />
              )}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
              <Button disabled={saving} onClick={submitBulk}
                className={decision === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
                data-testid="att-bulk-confirm">
                {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                {decision === 'approve' ? 'Approve Selected' : 'Disapprove Selected'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Decide — {dialog?.employee_name}</DialogTitle>
            <DialogDescription>
              {dialog?.date} · In {dialog?.check_in || '—'} / Out {dialog?.check_out || '—'} · {(dialog?.flags || []).join(', ')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-1">
            <div className="flex gap-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded border flex-1 hover:bg-emerald-50">
                <input type="radio" name="att-decision" checked={decision === 'approve'} onChange={() => setDecision('approve')} data-testid="att-decision-approve" />
                <span className="font-medium text-emerald-700">Approve</span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded border flex-1 hover:bg-red-50">
                <input type="radio" name="att-decision" checked={decision === 'reject'} onChange={() => setDecision('reject')} data-testid="att-decision-reject" />
                <span className="font-medium text-red-700">Disapprove</span>
              </label>
            </div>

            {decision === 'approve' ? (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Mark day as</Label>
                  <Select value={markAs} onValueChange={setMarkAs}>
                    <SelectTrigger data-testid="mark-as-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="present">Full Present (no deduction)</SelectItem>
                      <SelectItem value="half_day">Half Day (0.5 present in payroll)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Reason</Label>
                  <Select value={reasonPick} onValueChange={setReasonPick}>
                    <SelectTrigger data-testid="approve-reason-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {APPROVE_REASONS.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {reasonPick === 'Other' && (
                    <Input className="mt-2" placeholder="Enter reason" value={customReason}
                      onChange={(e) => setCustomReason(e.target.value)} data-testid="custom-reason-input" />
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Deduction</Label>
                  <Select value={deduction} onValueChange={setDeduction}>
                    <SelectTrigger data-testid="deduction-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lop">LOP — mark absent (deducted in payroll)</SelectItem>
                      <SelectItem value="half_day_leave">Half Day from leave balance (day stays paid)</SelectItem>
                      <SelectItem value="full_day_leave">Full Day from leave balance (day stays paid)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {deduction !== 'lop' && (
                  <div>
                    <Label className="text-xs">Deduct from leave type</Label>
                    <Select value={leaveType} onValueChange={setLeaveType}>
                      <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {leaveTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="text-xs">Remark (optional)</Label>
                  <Input placeholder="e.g. left without permission" value={customReason}
                    onChange={(e) => { setCustomReason(e.target.value); setReasonPick('Other'); }} data-testid="reject-remark-input" />
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-1">
              <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
              <Button onClick={submit} disabled={saving}
                className={decision === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
                data-testid="confirm-decision-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                {decision === 'approve' ? 'Approve' : 'Disapprove'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AttendancePendingApprovals;
